<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesCard from 'dashboard/components-next/Companies/CompaniesCard/CompaniesCard.vue';
import CompanyEmptyState from 'dashboard/components-next/Companies/EmptyState/CompanyEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CreateCompanyDialog from 'dashboard/components-next/Companies/CompaniesForm/CreateCompanyDialog.vue';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const companies = computed(() => companiesStore.getCompaniesList);
const meta = computed(() => companiesStore.getMeta);
const uiFlags = computed(() => companiesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(companySortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);

const hasCompanies = computed(() => companies.value.length > 0);
const isCompanyIndexView = computed(
  () => route.name === 'companies_dashboard_index' && pageNumber.value === 1
);

const showEmptyStateLayout = computed(() => {
  return !searchQuery.value && !hasCompanies.value && isCompanyIndexView.value;
});

const showEmptyText = computed(() => {
  return (searchQuery.value || !isCompanyIndexView.value) && !hasCompanies.value;
});

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const sortParam = computed(() => buildSortAttr());

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  // Only update URL if arguments were explicitly provided
  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  if (currentSearch) {
    await companiesStore.search({
      search: currentSearch,
      page: currentPage,
      sort: currentSort,
    });
  } else {
    await companiesStore.get({
      page: currentPage,
      sort: currentSort,
    });
  }
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    companies_sort_by: buildSortAttr(),
  });

  fetchCompanies(1, searchValue.value, buildSortAttr());
};

const handleShowCompany = companyId => {
  router.push(`/app/accounts/${route.params.accountId}/companies/${companyId}`);
};

const expandedCompanyId = ref(null);

const toggleExpand = companyId => {
  expandedCompanyId.value = expandedCompanyId.value === companyId ? null : companyId;
};

const createCompanyDialogRef = ref(null);

const handleAddCompany = () => {
  createCompanyDialogRef.value?.open();
};

const handleCreateCompany = async (companyData) => {
  try {
    await companiesStore.create(companyData);
    createCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.CREATE_ERROR'));
  }
};

const emptyStateMessage = computed(() => {
  if (!searchQuery.value) {
    return t('COMPANIES.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  }
  return t('COMPANIES.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchCompanies();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <CompaniesListLayout
      :search-value="searchValue"
      :header-title="t('COMPANIES.HEADER')"
      :current-page="pageNumber"
      :total-items="Number(meta.totalCount || 0)"
      :active-sort="activeSort"
      :active-ordering="activeOrdering"
      :is-fetching-list="isFetchingList"
      :show-pagination-footer="!!companies.length"
      @update:current-page="onPageChange"
      @update:sort="handleSort"
      @search="onSearch"
      @add="handleAddCompany"
    >
      <div
        v-if="isFetchingList"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <CompanyEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="t('COMPANIES.EMPTY_STATE.TITLE')"
          :subtitle="t('COMPANIES.EMPTY_STATE.SUBTITLE')"
          :button-label="t('COMPANIES.EMPTY_STATE.BUTTON_LABEL')"
          @create="handleAddCompany"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ emptyStateMessage }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-4 px-6 pt-4 pb-6">
          <CompaniesCard
            v-for="company in companies"
            :id="company.id"
            :key="company.id"
            :name="company.name"
            :domain="company.domain"
            :contacts-count="company.contactsCount || 0"
            :description="company.description"
            :avatar-url="company.avatarUrl"
            :updated-at="company.updatedAt"
            :is-expanded="expandedCompanyId === company.id"
            @toggle="toggleExpand(company.id)"
            @show-company="handleShowCompany"
          />
        </div>
      </template>
    </CompaniesListLayout>
    
    <CreateCompanyDialog
      ref="createCompanyDialogRef"
      @create="handleCreateCompany"
    />
  </div>
</template>
